import { Env } from '@env';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import * as Clipboard from 'expo-clipboard';
import * as DocumentPicker from 'expo-document-picker';
import { Image } from 'expo-image';
import { Redirect } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useAdmin } from '@/api/partners/admins/use-admin';
import {
  type OrganizationAttachment,
  useCreateOrganization,
} from '@/api/partners/organizations/use-create-organization';
import { useHasOrganization } from '@/core';
import { GREY_20, GREY_90 } from '@/style/colors';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Copy } from '@/ui/icons/copy';
import { Share } from '@/ui/icons/share';
import { Upload } from '@/ui/icons/upload';
import { Loader } from '@/ui/loader';

const portalPreviews = {
  map: {
    dark: require('../../../public/images/partners/eon-one/portal/map-dark.png'),
    light: require('../../../public/images/partners/eon-one/portal/map-light.png'),
  },
  list: {
    dark: require('../../../public/images/partners/eon-one/portal/list-dark.png'),
    light: require('../../../public/images/partners/eon-one/portal/list-light.png'),
  },
  subscription: {
    dark: require('../../../public/images/partners/eon-one/portal/subscription-dark.png'),
    light: require('../../../public/images/partners/eon-one/portal/subscription-light.png'),
  },
} as const;

// Web/desktop only: turn a picked document into an OrganizationAttachment whose
// `file` is a real File, which the create-organization mutation requires.
const normalizeWebDocumentAsset = async (
  asset: DocumentPicker.DocumentPickerAsset
): Promise<OrganizationAttachment> => {
  if (!asset.name || !asset.mimeType) {
    throw new Error('Web upload requires file name and mime type.');
  }

  const response = await fetch(asset.uri);
  const blob = await response.blob();
  const file = new File([blob], asset.name, { type: asset.mimeType });

  return {
    uri: asset.uri,
    name: asset.name,
    type: asset.mimeType,
    file,
  };
};

const SLUG_MAX_LENGTH = 63;

const sanitizeSlugInput = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, SLUG_MAX_LENGTH);

const slugFromHostOrValue = (value: string) =>
  sanitizeSlugInput(value.split('.')[0] ?? '');

const getPortalOrigin = (slug: string) => {
  const protocol = Platform.OS === 'web' ? window.location.protocol : 'https:';
  const port =
    Platform.OS === 'web' && window.location.port
      ? `:${window.location.port}`
      : '';

  return `${protocol}//${slug}.${Env.HOSTNAME}${port}`;
};

const copyLink = async (url: string) => {
  const copied = await Clipboard.setStringAsync(url);

  if (!copied) {
    showErrorMessage('Issue copying to clipboard');
    return;
  }

  Toast.show({ type: 'success', text1: 'Link copied to clipboard' });
};

const openLink = (url: string) => {
  if (Platform.OS === 'web') {
    window.open(url, '_blank');
  }
};

// background_1 per theme (the surface the logo actually renders on, e.g. the
// navbar). The dark-mode asset previews on the dark background and the
// light-mode asset on the light one, regardless of the current app theme.
const PREVIEW_BACKGROUND = {
  dark: GREY_90,
  light: GREY_20,
} as const;

const AssetUpload = ({
  label,
  previewUri,
  previewMode,
  onPress,
  size = 166,
  contentSize,
}: {
  label: string;
  previewUri?: string;
  previewMode: 'dark' | 'light';
  onPress: () => void;
  size?: number;
  // Caps how large the image renders inside the box. For small assets (e.g. a
  // 32px favicon) this avoids upscaling the image to fill the box, which makes
  // it look grainy/blurry.
  contentSize?: number;
}) => {
  const { theme } = useStyles();

  const innerSize = Math.min(contentSize ?? size, size);

  return (
    <View style={{ gap: 8 }}>
      <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
        {label}
      </Text>

      {previewUri ? (
        <Pressable
          onPress={onPress}
          style={{
            width: size,
            height: size,
            borderRadius: 8,
            overflow: 'hidden',
            borderWidth: 1,
            borderColor: theme.colors.card.default.borderColor,
            backgroundColor: PREVIEW_BACKGROUND[previewMode],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Image
            source={{ uri: previewUri }}
            style={{ width: innerSize, height: innerSize }}
            contentFit="contain"
          />
        </Pressable>
      ) : (
        <Button
          type="secondary"
          text="Upload"
          onPress={onPress}
          style={{ width: size, height: size }}
          rightIcon={
            <Icon type="naked" icon={<Upload />} width={16} height={16} />
          }
        />
      )}
    </View>
  );
};

const PortalLinkRow = ({
  title,
  description,
  url,
  preview,
}: {
  title: string;
  description: string;
  url: string | null;
  preview: { dark: number; light: number };
}) => {
  const { styles, theme } = useStyles(stylesheet);
  const themeName = UnistylesRuntime.themeName === 'dark' ? 'dark' : 'light';
  const displayUrl =
    url?.replace(/^https?:\/\//, '') ?? 'Set a subdomain first';

  return (
    <View style={styles.portalRow}>
      <Image
        source={preview[themeName]}
        style={styles.portalPreview}
        contentFit="cover"
      />

      <View style={styles.portalBody}>
        <View style={styles.portalCopy}>
          <Text type="bodyRegular">{title}</Text>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {description}
          </Text>
        </View>

        <View style={styles.portalFooter}>
          <Text type="caption1" numberOfLines={1} style={styles.portalUrl}>
            {displayUrl}
          </Text>

          <Pressable
            onPress={() => {
              if (url) copyLink(url);
            }}
            disabled={!url}
            style={styles.portalAction}
          >
            <Icon
              type="naked"
              icon={<Copy />}
              width={12}
              height={14}
              fillColor={theme.colors.text.primary}
            />
            <Text type="caption2">Copy</Text>
          </Pressable>

          <Pressable
            onPress={() => {
              if (url) openLink(url);
            }}
            disabled={!url}
            style={styles.portalAction}
          >
            <Icon
              type="naked"
              icon={<Share />}
              width={12}
              height={14}
              fillColor={theme.colors.text.primary}
            />
            <Text type="caption2">Open</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

export default function EonOneSettings() {
  const { styles, theme } = useStyles(stylesheet);
  const { organization, canUseEonOne, isPending } = useHasOrganization();
  const queryClient = useQueryClient();

  const { mutateAsync: upsertOrganization, isPending: isPublishing } =
    useCreateOrganization();

  const [orgSlug, setOrgSlug] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  const [darkLogo, setDarkLogo] = useState<OrganizationAttachment>();
  const [lightLogo, setLightLogo] = useState<OrganizationAttachment>();
  const [darkFavicon, setDarkFavicon] = useState<OrganizationAttachment>();
  const [lightFavicon, setLightFavicon] = useState<OrganizationAttachment>();

  // Hydrate the editable text fields once the admin's organization resolves
  // (and again after a publish refetch) so the form reflects saved state.
  useEffect(() => {
    if (organization) {
      setOrgSlug(slugFromHostOrValue(organization.urlSlug || ''));
      setSeoKeywords(
        Array.isArray(organization.keywords)
          ? organization.keywords.join(', ')
          : organization.keywords || ''
      );
    }
  }, [organization]);

  const pickAsset = async (setter: (asset: OrganizationAttachment) => void) => {
    if (Platform.OS !== 'web') return;

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/png', 'image/jpeg', 'image/svg+xml', 'image/x-icon'],
        copyToCacheDirectory: false,
        multiple: false,
      });

      if (!result.canceled) {
        setter(await normalizeWebDocumentAsset(result.assets[0]));
      }
    } catch (error) {
      console.error('Error picking file:', error);
    }
  };

  const handlePublish = async () => {
    if (!organization) return;

    try {
      // create-organization is an upsert on the backend. Pass through the
      // existing name/description so they aren't cleared, and only the assets
      // that were re-selected are uploaded.
      await upsertOrganization({
        name: organization.name,
        description: organization.description,
        urlSlug: slugFromHostOrValue(orgSlug || organization.urlSlug),
        keywords: seoKeywords
          .split(',')
          .map((keyword) => keyword.trim())
          .filter(Boolean),
        darkLogo,
        lightLogo,
        darkFavicon,
        lightFavicon,
      });

      await queryClient.invalidateQueries({ queryKey: useAdmin.getKey() });

      Toast.show({ type: 'success', text1: 'Changes published' });
    } catch (error) {
      console.error('Error publishing changes:', error);
    }
  };

  const slug = slugFromHostOrValue(organization?.urlSlug || orgSlug);
  const portalOrigin = slug ? getPortalOrigin(slug) : null;
  const lastUpdatedLabel = useMemo(() => {
    if (!organization?.urlSlugUpdatedAt) return 'N/A';

    return format(
      parseISO(organization.urlSlugUpdatedAt),
      "MMMM d, yyyy 'at' h:mm a"
    );
  }, [organization?.urlSlugUpdatedAt]);

  // Wait for the admin query to resolve before deciding. On a hard refresh the
  // query is briefly pending, so redirecting on `!organization` here would kick
  // an org-owning admin back to the join screen.
  if (isPending) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (!canUseEonOne) {
    return <Redirect href="/partner/join-eon-one" />;
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ gap: 56 }}>
      <View
        style={{
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <View style={{ gap: 4 }}>
          <Text type="title1">EonOne Settings</Text>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {`Last Updated ${lastUpdatedLabel}`}
          </Text>
        </View>

        <Button
          type="primarySmall"
          text="Publish"
          onPress={handlePublish}
          loading={isPublishing}
          disabled={isPublishing}
          style={{ width: 158 }}
        />
      </View>

      <View style={{ width: 696, gap: 32 }}>
        <Card style={{ padding: 16, gap: 32 }}>
          <Text type="title1">{`Booking Portal Settings`}</Text>

          <View style={{ gap: 16 }}>
            <View style={styles.subdomainRow}>
              <View style={styles.currentSubdomain}>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Current subdomain
                </Text>
                <View style={styles.currentSubdomainValue}>
                  <Text
                    type="bodyRegular"
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={styles.currentSubdomainHost}
                  >
                    {slug ? `${slug}.${Env.HOSTNAME}` : `—.${Env.HOSTNAME}`}
                  </Text>
                  <Pressable
                    onPress={() => {
                      if (portalOrigin) copyLink(portalOrigin);
                    }}
                    disabled={!portalOrigin}
                    hitSlop={8}
                  >
                    <Copy
                      width={12}
                      height={14}
                      color={theme.colors.text.secondary}
                      fill={theme.colors.text.secondary}
                    />
                  </Pressable>
                </View>
              </View>

              <View style={styles.editSubdomain}>
                <View style={styles.editSubdomainField}>
                  <Text type="caption1" style={styles.editSubdomainLabel}>
                    Edit your custom subdomain
                  </Text>
                  <Input
                    label=""
                    placeholder="Add custom subdomain"
                    value={orgSlug}
                    maxLength={SLUG_MAX_LENGTH}
                    autoCapitalize="none"
                    autoCorrect={false}
                    onChangeText={(text) => {
                      setOrgSlug(sanitizeSlugInput(text));
                    }}
                    containerStyle={{ flex: 1, minWidth: 0 }}
                  />
                </View>

                <Text type="caption1" style={{ marginTop: 24 }}>
                  .app.eonrides.com
                </Text>
              </View>
            </View>

            <Text type="bodyBold" style={{ marginTop: 16 }}>
              Booking Portal Views
            </Text>

            <View style={styles.viewPreferences}>
              <View style={styles.viewSectionHeader}>
                <Text type="bodyBold">Daily Rentals</Text>
              </View>

              <View style={styles.viewSectionBody}>
                <PortalLinkRow
                  title="Map View"
                  description="Full view of all your available cars on all cities on the map"
                  url={portalOrigin ? `${portalOrigin}/search?view=map` : null}
                  preview={portalPreviews.map}
                />

                <View style={styles.viewDivider} />

                <PortalLinkRow
                  title="List View"
                  description="One city's cars in a list, no map view"
                  url={portalOrigin ? `${portalOrigin}/search?view=list` : null}
                  preview={portalPreviews.list}
                />
              </View>

              <View
                style={[
                  styles.viewSectionHeader,
                  styles.viewSectionHeaderNested,
                ]}
              >
                <Text type="bodyBold">Subscription</Text>
              </View>

              <View
                style={[styles.viewSectionBody, styles.viewSectionBodyLast]}
              >
                <PortalLinkRow
                  title="Subscription link"
                  description="Tailor-made weekly and monthly EV subscriptions"
                  url={portalOrigin ? `${portalOrigin}/subscriptions` : null}
                  preview={portalPreviews.subscription}
                />
              </View>
            </View>

            <Input
              label="SEO keywords (Comma separated)"
              placeholder="Add SEO keywords"
              value={seoKeywords}
              onChangeText={(text) => {
                setSeoKeywords(text);
              }}
            />
          </View>
        </Card>

        <Card style={{ padding: 16, gap: 24 }}>
          <View>
            <Text type="title2">Assets</Text>
          </View>

          <View>
            <Text type="bodyRegular">Logo</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Recommended: 400x400px, PNG with transparent background
            </Text>
          </View>

          <View style={{ flexDirection: 'row', gap: 16 }}>
            <AssetUpload
              label="Dark mode"
              previewMode="dark"
              previewUri={darkLogo?.uri || organization.darkLogo}
              onPress={() => pickAsset(setDarkLogo)}
            />

            <AssetUpload
              label="Light mode"
              previewMode="light"
              previewUri={lightLogo?.uri || organization.lightLogo}
              onPress={() => pickAsset(setLightLogo)}
            />
          </View>

          <View>
            <Text type="bodyRegular">Favicon</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Recommended: 64x64px (or 32x32px), PNG or ICO
            </Text>
          </View>

          <View style={{ flexDirection: 'row', gap: 16 }}>
            <AssetUpload
              label="Dark mode"
              previewMode="dark"
              previewUri={darkFavicon?.uri || organization.darkFavicon}
              onPress={() => pickAsset(setDarkFavicon)}
              size={125}
              contentSize={64}
            />

            <AssetUpload
              label="Light mode"
              previewMode="light"
              previewUri={lightFavicon?.uri || organization.lightFavicon}
              onPress={() => pickAsset(setLightFavicon)}
              size={125}
              contentSize={64}
            />
          </View>
        </Card>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    padding: 56,
    backgroundColor: theme.colors.background_1,
  },
  subdomainRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  currentSubdomain: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  currentSubdomainValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
  },
  currentSubdomainHost: {
    flexShrink: 1,
    minWidth: 0,
  },
  editSubdomain: {
    flex: 1,
    minWidth: 220,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  editSubdomainField: {
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  editSubdomainLabel: {
    paddingLeft: 4,
    paddingRight: 4,
    color: theme.colors.text.secondary,
  },
  viewPreferences: {
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: theme.colors.card.default.backgroundColor,
  },
  viewSectionHeader: {
    backgroundColor: theme.colors.card.hover.backgroundColor,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.hover.borderColor,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  viewSectionHeaderNested: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.hover.borderColor,
  },
  viewSectionBody: {
    padding: 16,
    gap: 16,
  },
  viewSectionBodyLast: {
    paddingBottom: 16,
  },
  viewDivider: {
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  portalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  portalPreview: {
    width: 253,
    height: 142,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
  },
  portalBody: {
    flex: 1,
    height: 142,
    justifyContent: 'space-between',
  },
  portalCopy: {
    gap: 4,
  },
  portalFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  portalUrl: {
    flex: 1,
  },
  portalAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
}));
